// CI runs data layer (arc 101 slice 07 — the self-hosted "Blacksmith
// competitor"). Reads `GET /v1/ci/runs` — the account's CI runs, newest-first —
// and mints a short-lived presigned log url on demand
// (`GET /v1/ci/runs/:id/log-url`). Read-only; mirrors lib/fleet — one round-trip
// over `apiFetch` reusing the dev session bearer.

import type { CiRefusalReason, CiRunWire } from '@developerz/domain';
import { CiRunWireSchema } from '@developerz/domain';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { createLiveQuery } from './live/live-query';
import { SESSION_TOKEN } from './session';

/**
 * The runs body, PARSED (plan 101 slice 15 step 3). Cursorless on purpose: this
 * route answers ONE newest-first slice and mints no `next_cursor`, so the
 * envelope is `{ data }` alone and `pageSchema` would demand a key the door does
 * not send.
 */
const ciRunsSchema = z.object({ data: z.array(CiRunWireSchema) });

/**
 * Why a `refused` run was declined, IMPORTED from the domain rather than
 * restated. The hand-written union here named two values and the platform can
 * only produce one.
 *
 * `no_capacity` was the phantom: it is a WORKER dispatch decision that leaves the
 * job `queued` — explicitly "never refused", audited as `ci.job.no_capacity` and
 * retried on a later pass (`apps/worker/src/dispatch/ci-assigner.ts`;
 * `apps/runner/e2e/ci.e2e.test.ts:580` states the contrast). It is not a refusal
 * at all, so `CiRefusalReasonSchema` carries only `permission_missing`, and
 * `apps/api` serializes every CI row through `CiRunResource` — meaning no `/v1`
 * body has ever carried the value this client rendered a badge, a tone and an
 * i18n key for. Invisible, because `apiFetch` ended in a cast.
 *
 * Surfacing a capacity gap is a real thing to want, and it is a WIRE change (a
 * queued-run hint), not a refusal reason resurrected here.
 */
export type { CiRefusalReason };

/**
 * One CI-run row — `CiRunWireSchema` from `@developerz/domain`, THE PUBLISHED
 * RESOURCE, which `apps/api` serializes every row through (`CiRunResource`, in
 * `routes/ci.ts` and `index.ts`). No second declaration left to drift.
 *
 * The mirror it replaces was short FOUR fields the server projects on every row
 * — `github_run_id`, `queued_iso`, `assigned_iso` and `duration_ms` — and
 * carried one the server cannot send (see {@link CiRefusalReason}). That is the
 * same defect `RunnerRow` had, from the same cause: the door ends in a cast, so
 * nothing between the projection and this file could fail.
 *
 * The object key never crosses the boundary — `has_log` says whether a presigned
 * log url can be minted; `log_excerpt` is the `<2KB` failure tail (null until
 * logs land). Timestamps arrive as ISO strings.
 */
export type CiRunRow = Readonly<CiRunWire>;

export const CI_RUNS_QUERY_KEY = 'ci_runs';

/**
 * The caller's CI runs, newest-first, kept fresh by the account stream's `ci`
 * topic (`createLiveQuery`: a change re-reads; the 30 s poll runs only while the
 * stream does not vouch). Idle (no fetch) with no session.
 */
export function useCiRuns(token: string | null = SESSION_TOKEN) {
  return createLiveQuery<readonly CiRunRow[]>(
    () => ({
      queryKey: [CI_RUNS_QUERY_KEY, token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'ci_run' }],
      // Canonical list shape (`{ data, next_cursor }`) — one newest-first slice.
      queryFn: async () =>
        (await apiFetch('/v1/ci/runs', token, undefined, { schema: ciRunsSchema })).data,
    }),
    token,
  ).query;
}

/**
 * Mint + open one CI run's archived log in a new tab. Fetches the short-lived
 * presigned url (`GET /v1/ci/runs/:id/log-url`) on click — never cached in a query,
 * since it expires — then navigates the tab to it. The tab is opened SYNCHRONOUSLY
 * within the click gesture (before the `await`) so the browser keeps the user
 * activation and never blocks it as a popup; it's navigated once the url resolves,
 * and closed if the request fails. Throws `ApiError` (404) when no log is available
 * (unknown run / logs not landed / 3-day expiry); the caller surfaces it.
 */
export async function openCiLogUrl(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  // `noopener` would null the handle, and we need it to navigate the tab after the
  // fetch — so open plain and sever `opener` ourselves (reverse-tabnabbing guard).
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  try {
    const { url } = await apiFetch<{ url: string }>(`/v1/ci/runs/${id}/log-url`, token);
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer'); // pre-fetch popup block — best-effort retry
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/**
 * Human duration of a run (#715): completed→"3m 12s"; still running→elapsed
 * from `now`; not started→null. Pure for tests.
 */
export function ciDuration(run: CiRunRow, now: Date = new Date()): string | null {
  if (!run.started_iso) return null;
  const start = Date.parse(run.started_iso);
  const end = run.completed_iso ? Date.parse(run.completed_iso) : now.getTime();
  const secs = Math.max(0, Math.round((end - start) / 1000));
  const m = Math.floor(secs / 60);
  const sec = secs % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}
